'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatedGradientButton } from '@/app/[locale]/(worlds)/[roomId]/components/AnimatedGradientButton';
import PlayCircleIcon from '@/app/[locale]/(worlds)/[roomId]/components/PlayCircleIcon';
import IconGamepad from '@/components/assets/icon_gamepad.svg';
import IconLock from '@/components/assets/icon_lock.svg';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useMobilePlayCtaPlacementContext } from '@/context/MobilePlayCtaPlacementContext';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import { useMobilePlayCtaVariant } from '@/hooks/useMobilePlayCtaVariant';
import { useMobileWorldRedirectUrlBuilder } from '@/hooks/useMobileWorldRedirectUrl';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
type WorldPlayButtonMobileProps = {
  buttonClassName?: string;
  className?: string;
  playLabelKey?: string;
  showPlayIcon?: boolean;
  trailingContent?: ReactNode;
};
const gradientPresets = {
  play: [{
    background: 'linear-gradient(270deg, #7B74FF 0%, #00A0CB 53.38%, #00A0CB 100%)',
    color: 'rgba(255,255,255,0.4)'
  }, {
    background: 'linear-gradient(270deg, #05ECE1 0%, #00A0CB 50.47%, #7B74FF 100%)',
    color: 'rgba(255,255,255,1)'
  }],
  premium: [{
    background: 'linear-gradient(270deg, #FF82FB 0%, #00A0CB 58.81%, #00A0CB 100%)',
    color: 'rgba(255,255,255,0.4)'
  }, {
    background: 'linear-gradient(270deg, #05ECE1 0%, #00B3E3 38.45%, #FF82FB 100%)',
    color: 'rgba(255,255,255,1)'
  }]
};
export default function WorldPlayButtonMobile(t0) {
  const $ = _c(38);
  const {
    buttonClassName,
    className,
    playLabelKey: t1,
    showPlayIcon: t2,
    trailingContent
  } = t0;
  const playLabelKey = t1 === undefined ? "world-info.play" : t1;
  const showPlayIcon = t2 === undefined ? true : t2;
  const t = useTranslations();
  const {
    isVoucherCheckPassed,
    roomData
  } = useCheckEnterRoomContext();
  const {
    showMobilePlayButton
  } = useWorldDetailsContext();
  const {
    ctaBannerOffset,
    playPosition
  } = useMobilePlayCtaPlacementContext();
  const {
    captureAttributes,
    capturePlayClick,
    isIconLabel,
    isIconOnly
  } = useMobilePlayCtaVariant();
  const getRedirectUrl = useMobileWorldRedirectUrlBuilder();
  const communityUserId = roomData.owner.community_user_id;
  const t3 = `999999px 0px -${ctaBannerOffset}px 0px`;
  let t4;
  if ($[0] !== t3) {
    t4 = {
      threshold: 1,
      rootMargin: t3,
      initialIsIntersecting: true
    };
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    ref: homeRef,
    isIntersecting: isHomeClear,
    entry
  } = useIntersectionObserver(t4);
  if (!showMobilePlayButton) {
    return null;
  }
  if (isIconOnly && isVoucherCheckPassed !== false) {
    return trailingContent ?? null;
  }
  let t5;
  if ($[2] !== buttonClassName || $[3] !== captureAttributes || $[4] !== capturePlayClick || $[5] !== communityUserId || $[6] !== getRedirectUrl || $[7] !== isIconLabel || $[8] !== isVoucherCheckPassed || $[9] !== playLabelKey || $[10] !== showPlayIcon || $[11] !== t) {
    t5 = isVoucherCheckPassed === false ? <AnimatedGradientButton icon={<IconLock className="size-6 fill-white" />} label={t("world-info.get-premium")} linkTo={`/profile/${communityUserId}?premium=true`} gradients={gradientPresets.premium} buttonClassName={buttonClassName} /> : <AnimatedGradientButton icon={isIconLabel ? <PlayCircleIcon className="size-6 bg-white" triangleClassName="size-4" /> : showPlayIcon ? <IconGamepad className="size-6 fill-white" /> : undefined} biKey="world_mobile_play" captureAttributes={captureAttributes} label={t(playLabelKey)} linkTo={getRedirectUrl({
      isFull3d: true
    })} onClick={() => {
      capturePlayClick();
      window.location.href = getRedirectUrl({
        isFull3d: true
      });
    }} gradients={gradientPresets.play} buttonClassName={buttonClassName} />;
    $[2] = buttonClassName;
    $[3] = captureAttributes;
    $[4] = capturePlayClick;
    $[5] = communityUserId;
    $[6] = getRedirectUrl;
    $[7] = isIconLabel;
    $[8] = isVoucherCheckPassed;
    $[9] = playLabelKey;
    $[10] = showPlayIcon;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const playButton = t5;
  if (playPosition === "test") {
    const isDocked = ctaBannerOffset > 0 && !isHomeClear;
    let t6;
    if ($[13] !== entry?.boundingClientRect || $[14] !== isDocked) {
      t6 = isDocked ? {
        minHeight: entry?.boundingClientRect.height
      } : undefined;
      $[13] = entry?.boundingClientRect;
      $[14] = isDocked;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    const t7 = isDocked ? "z-floating fixed inset-x-0 flex justify-center px-4" : undefined;
    let t8;
    if ($[16] !== ctaBannerOffset || $[17] !== isDocked) {
      t8 = isDocked ? {
        bottom: ctaBannerOffset
      } : undefined;
      $[16] = ctaBannerOffset;
      $[17] = isDocked;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    const t9 = isDocked ? "w-full max-w-[358px]" : undefined;
    let t10;
    if ($[19] !== playButton || $[20] !== t9) {
      t10 = <div className={t9}>{playButton}</div>;
      $[19] = playButton;
      $[20] = t9;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== t10 || $[23] !== t7 || $[24] !== t8) {
      t11 = <div className={t7} style={t8}>{t10}</div>;
      $[22] = t10;
      $[23] = t7;
      $[24] = t8;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== homeRef || $[27] !== t11 || $[28] !== t6) {
      t12 = <div ref={homeRef} style={t6}>{t11}</div>;
      $[26] = homeRef;
      $[27] = t11;
      $[28] = t6;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== className || $[31] !== t12 || $[32] !== trailingContent) {
      t13 = <div className={className}>{t12}{trailingContent}</div>;
      $[30] = className;
      $[31] = t12;
      $[32] = trailingContent;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    return t13;
  }
  if (!trailingContent) {
    return playButton;
  }
  let t6;
  if ($[34] !== className || $[35] !== playButton || $[36] !== trailingContent) {
    t6 = <div className={className}>{playButton}{trailingContent}</div>;
    $[34] = className;
    $[35] = playButton;
    $[36] = trailingContent;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  return t6;
}